import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import updateFilters from '@helpers/filters';
import { Brandfolder, Collection } from '@helpers/show_page_helpers';
import {
  add as addFilter,
  remove as removeFilter,
} from '@api/search-filters';
import {
  formatLabelsFlat,
  formatLabelsTree,
  get as getLabels,
} from '@api/v4/resources/labels';
import getResourceAssets from '@api/v4/resources/assets';
import getSearchableThings from '@api/searchable-things';
import { setLocalStorage } from '@helpers/local_storage';
import sanitize from '@helpers/sanitize';

import InternalShareBar from '@components/show_page/other/internal_share_bar';
import AdvancedDownloadModal from '@components/advanced_download/main';
import Web2PrintModal from '@components/web_2_print/web_2_print_modal';
import { ProcessingLoader } from '@components/common/loader/main';
import FetchControllersWrapper from '@components/common/fetch_controllers_wrapper';
import CustomDragLayer from './asset/custom_drag_layer';
import SectionsContext from './sectionsContext';
import SectionSearch from "./section_search/main";
import SectionSort from "./section_sort/main";
import PinnedSearches from "./pinned_searches/main";
import BulkSelectBar from "../bulk_actions/BulkSelectBar";
import FiltersDrawer from "./filters_drawer/main";
import LabelsBreadcrumbs from "./labels_breadcrumbs/main";
import Section from './section';
import filterLogic from './advanced_filters/filterLogic';

import './styles/show_page.scss';

// I'm SO SORRY. This is to make react-dnd work on IE 11.
// Unfortunately, I'm short on time here. The ideal solution would
// be to find a way to apply the babel-polyfill to that library
// specifically, but I'm coming up empty there. This works fine, tho.
if (!String.prototype.endsWith) {
  // eslint-disable-next-line no-extend-native
  String.prototype.endsWith = (search, this_len) => {
    if (this_len === undefined || this_len > this.length) {
      this_len = this.length; // eslint-disable-line no-param-reassign
    }
    return this.substring(this_len - search.length, this_len) === search;
  };
}

class ShowPage extends React.Component {
  constructor(props) {
    super(props);

    const {
      assets,
      defaultSortOrder
    } = this.props;

    const searchQuery = this.urlParams().q;

    this.state = {
      ...this.initialFiltersState(),
      activeLabelKey: null,
      activeSection: 'all',
      assets,
      filtersDrawerOpen: 'none',
      assetsProcessing: 0,
      labels: {},
      lastSelected: null,
      lastVisited: {},
      listView: this.setListView(),
      loading: true,
      searchableThings: null,
      searchKey: 'none',
      searchFilters: this.props.searchFilters,
      searchQuery,
      searchSubmitted: false,
      sections: {},
      selectedAssetKeys: new Set(),
      showAdvancedDownloadModal: false,
      showWebToPrintModal: false,
      sortBy: defaultSortOrder,
      entireSectionSelected: new Set(),
    };

    // So we can abort requests later if necessary;
    // it's not a piece of state because it won't have
    // any effect on re-rendering the component.
    this.activeRequests = {};
  }

  componentDidMount() {
    this.fetchSections().then(() => {
      this.fetchLabels();
      this.fetchSearchableThings();
      this.updateEventListeners('add');
    });
  }

  componentWillUnmount() {
    this.updateEventListeners('remove');
  }

  setListView() {
    const value = `; ${document.cookie}`;
    const parts = value.split("; Brandfolderlistview=");
    if (parts.length === 2 && parts.pop().split(';').shift() === 'true') {
      return true;
    }

    return false;
  }

  getSection(sectionKey) {
    const { sections, assets } = this.state;
    const result = sections[sectionKey];
    if (result) {
      return result;
    }

    return {
      currentPage: 1,
      total: assets[sectionKey] ? assets[sectionKey].length : 0,
      totalPages: 1,
      loading: false,
    };
  }

  get resourceKey() {
    return Collection.key ? Collection.key : Brandfolder.key;
  }

  get resourceType() {
    return Collection.key ? "Collection" : "Brandfolder";
  }

  updateEventListeners = (action) => {
    const triggerActivateLabel = ({ detail }) => {
      const labelkey = detail;
      this.activateLabel(labelkey);
    };

    const launchModal = (e, modalType) => {
      const { assetKey, attachmentKey } = e.detail;
      BF.dialog.dismiss();
      this.setState({
        lastVisited: { assetKey, attachmentKey },
        [modalType]: true,
      });
    };

    if (action === 'add') {
      if (!BFG.manifestDigest) {
        window.addEventListener('activateLabel', triggerActivateLabel);
      }
      window.addEventListener('launchAdvancedDownload', (e) => launchModal(e, 'showAdvancedDownloadModal'));
      window.addEventListener('launchWebToPrint', (e) => launchModal(e, 'showWebToPrintModal'));
    }

    if (action === 'remove') {
      if (!BFG.manifestDigest) {
        window.removeEventListener('activateLabel', triggerActivateLabel);
      }
      window.removeEventListener('launchAdvancedDownload', (e) => launchModal(e, 'showAdvancedDownloadModal'));
      window.removeEventListener('launchWebToPrint', (e) => launchModal(e, 'showWebToPrintModal'));
    }

    return undefined;
  }

  toggleSelected = (assetKeys, pageAndSectionInfo) => {
    this.setState(({ selectedAssetKeys, entireSectionSelected }) => {
      const fullySelectedSections = new Set(entireSectionSelected);
      const selectedKeys = new Set(selectedAssetKeys); // mutation is illegal!!!!
      const isArray = Array.isArray(assetKeys);
      const keys = isArray ? assetKeys : [assetKeys];

      let lastSelected;

      keys.forEach((assetKey) => {
        if (pageAndSectionInfo && selectedKeys.has(assetKey)) {
          if (!pageAndSectionInfo.selectCurrentPage && !pageAndSectionInfo.selectAllPages) {
            selectedKeys.delete(assetKey);
          }
        } else {
          selectedKeys.add(assetKey);
        }

        lastSelected = assetKey;
      });

      if (pageAndSectionInfo && pageAndSectionInfo.selectAllPages) {
        fullySelectedSections.add(pageAndSectionInfo.sectionKey);
      } else if (pageAndSectionInfo && pageAndSectionInfo.sectionKey) {
        fullySelectedSections.delete(pageAndSectionInfo.sectionKey);
      }

      return {
        selectedAssetKeys: selectedKeys,
        lastSelected,
        entireSectionSelected: fullySelectedSections,
      };
    });
  }

  toggleActiveSection = (sectionId) => {
    this.setState((state) => {
      const activeSection = state.activeSection === sectionId ? "all" : sectionId;
      return { activeSection };
    });
  }

  addRemovePinnedSearch = async () => {
    const { searchFilters, searchQuery } = this.state;
    if (searchFilters.map((filter) => filter.query).includes(searchQuery)) {
      const pinnedSearch = [...searchFilters].filter((item) => item.query === searchQuery);
      const updatedFilters = [...searchFilters].filter((filter) => (filter.key !== pinnedSearch[0].key));

      removeFilter(pinnedSearch[0].key);
      this.setState({ searchFilters: updatedFilters });
    } else {
      this.showFilterModal();
    }
  }

  submitSearchQuery = (filter, keypress, options = {}) => {
    let searchKey = options.searchKey || 'none';
    const { searchFilters } = this.props;

    this.setState({ loading: true }, () => {
      this.setState((state) => {
        const isPinnedSearch = searchFilters.filter((item) => item.query === filter);

        let searchQuery = filter === state.searchQuery && isPinnedSearch.length > 0 && !keypress ? null : filter;

        if (searchQuery && searchQuery.startsWith("FindAll: ")) {
          searchQuery = this.findAll(searchQuery);
        }

        const sections = ((!filter || filter === state.searchQuery) && !searchQuery) ? "all" : "";

        if (searchQuery) {
          Insight.trackSearchEvent(searchQuery);
        }

        if (!searchQuery) {
          // This may seem weird, but it's actually pretty great;
          // We use searchKey as the input to the `key` prop on the
          // section search. When it changes, the section search re-mounts
          // and updates its own internal state based on its props.
          // In cases where we clear the search, it's best to set it to
          // something unique to force this refresh. Random works pretty ok.
          searchKey = `none-${Math.random()}`;
        }

        return {
          searchQuery,
          searchSubmitted: true,
          activeSection: sections,
          recievedSections: 0,
          searchKey,
          sections: {},
        };
      }, () => {
        this.fetchSections();
      });
    });
  }

  resetFilters = () => {
    this.setState((state) => this.initialFiltersState(state), this.fetchSections);
  }

  toggleListView = () => {
    this.setState((prevState) => {
      document.cookie = `Brandfolderlistview=${!prevState.listView};`;
      return { listView: !prevState.listView };
    });
  }

  toggleFiltersDrawer = (source) => {
    this.setState((prevState) => {
      const { filtersDrawerOpen } = prevState;
      let updatedFiltersDrawerOpen = 'none';

      if (filtersDrawerOpen === 'none') { // was CLOSED
        if (source === 'labels-drawer') {
          updatedFiltersDrawerOpen = 'labels';
        } else {
          updatedFiltersDrawerOpen = 'advanced-filters';
        }
      }

      if (BFG.context.hasFeature('labels')) { // was OPEN
        if (source === 'labels-drawer' && filtersDrawerOpen === 'advanced-filters') {
          updatedFiltersDrawerOpen = 'labels';
        } else if (source === 'advanced-filters' && filtersDrawerOpen === 'labels') {
          updatedFiltersDrawerOpen = 'advanced-filters';
        } else if (source === 'section-search' && filtersDrawerOpen === 'labels') {
          updatedFiltersDrawerOpen = 'advanced-filters';
        }
      }

      setLocalStorage('labelsDrawerOpen', updatedFiltersDrawerOpen === 'labels');

      return { filtersDrawerOpen: updatedFiltersDrawerOpen };
    });
  }

  updateFilters = (filterType, keyValue, subValue) => {
    const { filters, filterStrings } = this.state;

    if (filterType === 'reset') {
      this.resetFilters();
      return;
    }

    const results = updateFilters({
      filters,
      filterStrings,
      filterType,
      keyValue,
      subValue,
    });

    this.setState({
      ...results,
      loading: true,
    }, this.fetchSections);
  }

  activateLabel = (labelId) => {
    this.setState({ activeLabelKey: labelId }, () => {
      this.fetchSections();
    });
  }

  updateSearchQuery = (e, searchQuery) => {
    const search = searchQuery || e.target.value;

    this.setState({
      searchQuery: search,
      searchKey: search,
    }, () => {
      this.fetchSections();
    });
  }

  updateSortOrder = (order) => {
    this.setState({ sortBy: order }, () => {
      this.fetchSections();
    });
  }

  addRemoveSelected = (assetKeys) => {
    this.setState(({ entireSectionSelected }) => ({
      selectedAssetKeys: new Set(assetKeys),
      entireSectionSelected: assetKeys.length ? entireSectionSelected : new Set(),
    }));
  }

  selectAll = () => {
    this.setState({ selectedAssetKeys: new Set(this.allAssetKeys()) });
  }

  shiftSelect = (assetKey) => {
    // TODO: just redo all of this :(
    const { lastSelected, selectedAssetKeys } = this.state;
    if (!lastSelected) {
      this.toggleSelected(assetKey);
      return undefined;
    }

    const allAssetKeys = this.allAssetKeys();
    const lastSelectedPosition = allAssetKeys.indexOf(lastSelected);
    const position = allAssetKeys.indexOf(assetKey);
    const selectedSet = new Set(selectedAssetKeys); // TODO: how do I get this, but ORDERED?

    if (position !== lastSelectedPosition) {
      allAssetKeys.forEach((asset, i) => {
        const assetPosition = i;

        if ((assetPosition > lastSelectedPosition && assetPosition <= position)
          || (assetPosition < lastSelectedPosition && assetPosition >= position)
        ) {
          if (selectedSet.has(asset)) {
            selectedSet.delete(asset);
          } else {
            selectedSet.add(asset);
          }
        }
      });
      this.addRemoveSelected(Array.from(selectedSet));
    } else {
      this.toggleSelected(assetKey);
    }
    this.setState({ lastSelected: assetKey });
    return undefined;
  }

  onNewAssets = (sectionKey, { data: newAssets }) => {
    this.setState((state) => {
      const { sections, assets } = state;
      const { paginatePer } = this.props;
      const sectionData = this.getSection(sectionKey);
      let { totalPages } = sectionData;
      const { total } = sectionData;

      let updatedAssets = [...assets[sectionKey]];

      const { selectedAssetKeys } = state;
      newAssets.forEach(({ id }) => selectedAssetKeys.add(id));

      BF.fx.updateAssetPageCount(newAssets.length, true);
      if (totalPages <= 1 && total < paginatePer) {
        const newTotal = total + newAssets.length;
        const differenceToAdd = paginatePer - total;

        const addMax = newAssets.length > differenceToAdd ? differenceToAdd : newAssets.length;
        updatedAssets = updatedAssets.concat(newAssets.slice(0, addMax));

        if (paginatePer < newTotal) {
          totalPages = Math.trunc(newTotal / paginatePer) + 1;
        }

        return {
          assets: {
            ...assets,
            [sectionKey]: updatedAssets,
          },
          sections: {
            ...sections,
            [sectionKey]: {
              ...sectionData,
              total: newTotal,
              totalPages,
            }
          },
          selectedAssetKeys,
        };
      }

      return { selectedAssetKeys };
    });
  }

  onAssetReorder = (sectionKey, assets) => {
    this.setState((state) => ({
      assets: {
        ...state.assets,
        [sectionKey]: assets,
      }
    }));
  }

  fetchLabels = () => {
    if (!BFG.context.hasFeature("labels") || BFG.manifestDigest) { return undefined; }

    getLabels().then((response) => {
      const labelsTree = formatLabelsTree(response);
      const labelsFlat = formatLabelsFlat(response);
      this.setState({
        labels: {
          labelsFlat,
          labelsResponse: response,
          labelsTree,
        }
      });
    });

    return undefined;
  }

  fetchSearchableThings = (clearCache = false) => {
    if (BFG.manifestDigest) { return undefined; }

    getSearchableThings({
      resourceType: window.SOURCE.resource_type,
      resourceKey: window.SOURCE.resource_key,
      clearCache,
    }).then((searchableThings) => { this.setState({ searchableThings }); });

    return undefined;
  }

  fetchSections = async () => {
    this.setState({ loading: true });
    const newAssets = {};
    const newSections = {};
    const { sections, sectionsToShow } = this.props;
    const promises = sections.map((section) => {
      const { section_key: sectionKey, id } = section;
      if (!sectionsToShow.includes(id)) { return undefined; }

      return this.updateSection(sectionKey, {
      }).then(({ assets, ...sectionData }) => {
        newAssets[sectionKey] = assets;
        newSections[sectionKey] = sectionData;
      });
    });

    await Promise.all(promises);

    this.setState({
      assets: newAssets,
      sections: newSections,
      loading: false,
    });
  }

  fetchSection = async (sectionKey, options = {}) => {
    const { currentPage } = this.state.sections[sectionKey] || {};

    const { page: page_ } = options;
    const page = page_ || currentPage;

    this.setState((state) => ({
      sections: {
        ...state.sections,
        [sectionKey]: {
          ...state.sections[sectionKey],
          loading: true,
        },
      },
    }));

    const { assets, ...sectionData } = await this.updateSection(sectionKey, {
      page,
    });

    this.setState((state) => ({
      assets: {
        ...state.assets,
        [sectionKey]: assets,
      },
      sections: {
        ...state.sections,
        [sectionKey]: {
          ...sectionData,
          loading: false,
        },
      },
    }));
  }

  updateSection = (sectionKey, options = {}) => {
    const {
      activeLabelKey,
      filters,
      searchQuery,
      sortBy,
    } = this.state;
    const { page: page_ } = options;

    const isCollection = Collection.key;
    const { currentPage } = this.state.sections[sectionKey] || {};

    const page = page_ || currentPage;

    const [sort_by, order] = sortBy.split(' ');
    const params = {
      ...options,
      sort_by,
      order,
      page,
      digest: BF_Manifest,
      per: this.urlParams().per,
      strict_search: filters.strict,
      search: filterLogic(filters, searchQuery),
      fast_jsonapi: true,
      localUTC: Date.now()
    };

    const activeRequest = this.activeRequests[sectionKey];
    if (activeRequest) {
      if (!activeRequest.signal.aborted) {
        activeRequest.abort();
      }
    }
    const fetchController = (controller) => { this.activeRequests[sectionKey] = controller; };

    let fetchOptions = {
      searchQuery: Boolean(searchQuery),
      fetchController,
    };

    if (isCollection) {
      fetchOptions = {
        ...options,
        resourceType: 'collections',
        resourceKey: Collection.key,
      };
    }

    if (activeLabelKey) {
      params.labelKeys = [activeLabelKey];
    }

    return getResourceAssets(sectionKey, params, fetchOptions);
  }

  updateSearchFilters = (updatedSearchFilters) => {
    this.setState({ searchFilters: updatedSearchFilters });
  }

  initialFiltersState(state = {}) {
    let { filterStrings } = state;

    filterStrings = filterStrings || this.initialFilterStrings();

    if (filterStrings.includes('strict')) {
      filterStrings.splice(filterStrings.indexOf('strict'), 1);
    }

    return {
      filters: {
        orientation: [],
        created_at: null,
        availability: null,
        comments: null,
        tags: [],
        custom_fields: {},
        filetypes: [],
        strict: this.props.defaultStrictSearch,
        label: null,
      },
      filterStrings: this.initialFilterStrings(),
    };
  }

  urlParams() {
    const params = {};
    window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => params[key] = value); // eslint-disable-line no-return-assign
    return params;
  }

  showFilterModal() {
    const query = encodeURIComponent(this.state.searchQuery);
    const title = t('Create A New Pin Search');
    const base_url = "/api/search_filters/new";
    const token_param = `?token=${BF_Token}`;
    const query_param = `&query=${query}`;
    const resource_params = `&resource_key=${this.resourceKey}&resource_type=${this.resourceType}`;
    const url = base_url + token_param + query_param + resource_params;


    BF.dialog.render(title, url, () => {
      // TODO: implement this modal with react
      $('#dialog').find('.modal-dialog').removeClass('modal-lg').addClass('modal-md');
      $('.j-search-filter-submit').on('click', () => {
        addFilter({
          resourceKey: this.resourceKey,
          resourceType: this.resourceType,
          label: sanitize($('#search_filter_label').val()),
          query: sanitize($('#search_filter_query').val()),
        }).then((response) => {
          BF.dialog.dismiss();
          this.setState({ searchFilters: response });
        }).catch((response) => {
          if (response.responseJSON.label) {
            $('#search_filter_label').addClass('invalid');
          }

          if (response.responseJSON.query) {
            $('#search_filter_query').addClass('invalid');
          }
        });
      });
      $('#search_filter_query').on('keydown', () => {
        $(this).removeClass('invalid');
      });
      $('#search_filter_label').on('keydown', () => {
        $(this).removeClass('invalid');
      });
    });
  }

  initialFilterStrings() {
    return this.props.defaultStrictSearch ? ['strict'] : [];
  }

  allAssetKeys() {
    const sectionAssets = Object.values(this.state.assets);
    if (!sectionAssets.length) { return []; }

    return sectionAssets.reduce((acc, val) => acc.concat(val), []).map((asset) => asset.id);
  }

  sectionShouldRender(sectionKey) {
    const { sections, editable } = this.props;
    const {
      assets,
      loading,
      searchQuery,
      filterStrings,
      activeSection: activeSectionId,
    } = this.state;

    const activeSection = sections.find((section) => section.id.toString() === activeSectionId);
    const isActive = activeSection ? activeSection.section_key === sectionKey : false;
    const hasAssets = Boolean(assets[sectionKey]) && assets[sectionKey].length > 0;
    const noActiveNoSearch = !activeSection && !searchQuery && !filterStrings.length;

    return (editable && (noActiveNoSearch || loading || hasAssets || isActive))
      || (!editable && hasAssets);
  }

  renderSections() {
    const {
      editable,
      printuiSectionId,
      sections,
      sectionsToShow,
      authenticityToken,
      lazyLoadCards,
      enableContactSheets
    } = this.props;

    const {
      activeSection,
      selectedAssetKeys,
      assets,
      loading: allLoading,
      entireSectionSelected,
      filters,
      searchQuery,
    } = this.state;

    return sections.map((section, index) => {
      if (!editable && !sectionsToShow.includes(section.id)) { return undefined; }
      const sectionAssets = assets[section.section_key] || [];
      const {
        totalPages,
        currentPage,
        total,
        loading
      } = this.getSection(section.section_key);

      if (!this.sectionShouldRender(section.section_key)) {
        return undefined;
      }

      const isTest = window.BF_Environment === 'test';

      return (
        <Section
          assets={sectionAssets}
          activeSection={activeSection}
          editable={editable}
          key={section.section_key}
          printuiSectionId={printuiSectionId}
          section={section}
          selectedAssetKeys={selectedAssetKeys}
          shiftSelect={this.shiftSelect}
          toggleSelected={this.toggleSelected}
          onAssetProcessing={(quantity) => this.setState({ assetsProcessing: quantity })}
          onNewAssets={(assetData) => this.onNewAssets(section.section_key, assetData)}
          onUpdate={(options) => this.fetchSection(section.section_key, options)}
          totalPages={totalPages || 1}
          currentPage={currentPage}
          loading={allLoading || Boolean(loading)}
          totalAssets={total}
          authenticityToken={authenticityToken}
          onAssetReorder={this.onAssetReorder}
          isRendered={isTest ? true : index === 0}
          allAssetsSelected={entireSectionSelected.has(section.section_key)}
          searchQuery={filterLogic(filters, searchQuery)}
          lazyLoadCards={lazyLoadCards}
          enableContactSheets={enableContactSheets}
        />
      );
    });
  }

  renderFullQuery() {
    const { filters, searchQuery, showFullQuery } = this.state;
    if (showFullQuery) {
      return (
        <div className="filters-results-row query-row">
          <div className="filters-results-wrap">
            <div className="filters-results-list exact-query">
              Exact Query:
              {unescape(` ${filterLogic(filters, searchQuery)}`)}
            </div>
          </div>
        </div>
      );
    }

    return undefined;
  }

  renderFiltersString() {
    const { filterStrings, showFullQuery } = this.state;
    return (
      <React.Fragment>
        <div className="filters-results-row results-row">
          <div className="filters-results-wrap">
            <div className="filters-results-list show-results">
              {t('Showing Results for:')}
              &nbsp;
              {filterStrings.map((item, i) => (
                <span key={`item-${i}`}>
                  {item}
                  {i < filterStrings.length - 1 ? "," : ""}
                </span>
              ))}
            </div>
            <div className="clickable-options">
              <button
                type="button"
                className="exact-query-group"
                onClick={() => this.setState({ showFullQuery: !showFullQuery })}
              >
                <span
                  className="show-exact-query bff-eye"
                  aria-hidden="true"
                />
                <span>View Query</span>
              </button>
              <button
                type="button"
                onClick={this.resetFilters}
              >
                <span
                  className="clear-filters bff-close"
                  aria-hidden="true"
                />
                <span>Clear</span>
              </button>
            </div>
          </div>
        </div>
        { showFullQuery ? this.renderFullQuery() : ''}
      </React.Fragment>
    );
  }

  render() {
    const {
      approver,
      downloadable,
      editable,
      libraryName,
      parentBrandfolders,
      pinsDropdownSetting,
      sections,
      sectionDropdownSetting,
      sectionsToShow,
    } = this.props;

    const {
      activeLabelKey,
      activeSection,
      activeSearchFilter,
      filters,
      filtersDrawerOpen,
      filterStrings,
      assetsProcessing,
      labels,
      lastVisited,
      listView,
      loading,
      searchableThings,
      searchKey,
      searchFilters,
      searchQuery,
      searchSubmitted,
      selectedAssetKeys,
      showAdvancedDownloadModal,
      showWebToPrintModal,
      sortBy,
    } = this.state;

    const allAssetKeys = this.allAssetKeys();
    const totalAssets = allAssetKeys.length;
    const filtersDrawerClass = filtersDrawerOpen !== "none" ? "filters-open" : "";
    const labelsFeatureClass = BFG.context.hasFeature("labels") && labels && labels.labelsResponse && labels.labelsResponse.length
      ? "labels-feature-enabled"
      : '';

    const disabledSections = sections.filter((section) => (
      !this.sectionShouldRender(section.section_key)
    )).map((section) => section.section_key);

    let activeLabelName = null;
    if (labels.labelsFlat) {
      const label = labels.labelsFlat[activeLabelKey];
      activeLabelName = label ? label.name : null;
    }

    return (
      <SectionsContext.Provider
        value={{
          activeLabelKey,
          activeLabelName,
          downloadable,
          editable,
          labelsDrawerOpen: filtersDrawerOpen === 'labels',
          listView,
          parentBrandfolders,
          shiftSelect: this.shiftSelect,
          sortBy,
          submitSearchQuery: this.submitSearchQuery,
          toggleSelected: this.toggleSelected,
        }}
      >
        <CustomDragLayer selectedAssetKeys={selectedAssetKeys} />
        <div className="custom_section_title" />
        {!BFG.manifestDigest ? (
          <SectionSort
            activeSection={activeSection}
            disabledSections={disabledSections}
            editable={editable}
            manageSectionsFeature={BFG.context.hasFeature("manage_sections")}
            sectionDropdownSetting={sectionDropdownSetting}
            sections={sections}
            sectionsToShow={sectionsToShow}
            toggleActiveSection={this.toggleActiveSection}
          />
        ) : ''}
        {(editable || searchFilters.length > 0) ? (
          <PinnedSearches
            activeSearchFilter={activeSearchFilter}
            editable={editable}
            searchFilters={searchFilters}
            searchSubmitted={searchSubmitted}
            searchQuery={searchQuery}
            pinsDropdownSetting={pinsDropdownSetting}
            submitSearchQuery={this.submitSearchQuery}
            updateSearchFilters={this.updateSearchFilters}
          />
        ) : ''}
        <div className={`brandfolder-assets-wrapper ${filtersDrawerClass} ${labelsFeatureClass}`}>
          {!BFG.manifestDigest ? (
            <FiltersDrawer
              activateLabel={this.activateLabel}
              activeLabelKey={activeLabelKey}
              labels={labels}
              filters={filters}
              filtersDrawerOpen={filtersDrawerOpen}
              searchableThings={searchableThings}
              selectedAssetKeys={selectedAssetKeys}
              toggleFiltersDrawer={this.toggleFiltersDrawer}
              updateFilters={this.updateFilters}
              refresh={() => {
                this.setState({ searchableThings: null });
                this.fetchSearchableThings(true);
              }}
            />
          ) : null}
          <div className="asset-content">
            {!BFG.manifestDigest && activeLabelKey ? (
              <LabelsBreadcrumbs
                activateLabel={this.activateLabel}
                activeLabelKey={activeLabelKey}
                labelsFlat={labels.labelsFlat}
              />
            ) : ''}
            {!BFG.manifestDigest ? (
              // Note that the key here is important! Changing it causes the component to refresh,
              // which is how it receives new props, specifically the searchQuery when saved searches
              // are clicked. It's a little magical, but it works great!
              <SectionSearch
                addRemovePinnedSearch={this.addRemovePinnedSearch}
                editable={editable}
                searchFilters={searchFilters}
                searchSubmitted={searchSubmitted}
                searchQuery={searchQuery}
                submitSearchQuery={this.submitSearchQuery}
                toggleFiltersDrawer={this.toggleFiltersDrawer}
                toggleListView={this.toggleListView}
                updateSearchQuery={this.updateSearchQuery}
                updateSortOrder={this.updateSortOrder}
                key={searchKey}
                brandfolderKey={BF.fx.brandfolderID()} // TODO: don't rely on this
              />
            ) : ''}
            {!BFG.manifestDigest && filterStrings.length > 0 ? this.renderFiltersString() : ''}
            <InternalShareBar allAssetKeys={new Set(this.allAssetKeys())} />
            {!loading && (searchQuery && searchQuery.length > 0) && totalAssets === 0
              ? <div> No results found </div>
              : this.renderSections()}
            <BulkSelectBar
              activeLabelKey={activeLabelKey}
              addRemoveSelected={this.addRemoveSelected}
              approver={approver}
              assetsProcessing={assetsProcessing}
              editable={editable}
              labels={labels}
              libraryName={libraryName}
              selectAll={this.selectAll}
              selectedAssetKeys={selectedAssetKeys}
              totalAssets={totalAssets}
            />
            {BFG.context.hasFeature("web_to_print") && showWebToPrintModal && (
              <FetchControllersWrapper>
                <Web2PrintModal
                  assetKey={lastVisited.assetKey}
                  collectionSlug={Collection.slug || null}
                  launchedFromModal
                  resetShowModal={() => this.setState({ showWebToPrintModal: false })}
                />
              </FetchControllersWrapper>
            )}
            {BFG.context.hasFeature("cropping") && showAdvancedDownloadModal && (
              <AdvancedDownloadModal
                lastVisited={lastVisited}
                resetShowModal={() => this.setState({ showAdvancedDownloadModal: false })}
                resource={{ type: this.resourceType.toLowerCase(), key: this.resourceKey }}
                shouldCloseOnOverlayClick={false}
              />
            )}
          </div>
        </div>
        <div className={classnames("processing-loader-container",
          {
            active: assetsProcessing > 0,
            "bulk-select-bar-active": this.state.selectedAssetKeys.size > 0,
          })}
        >
          {assetsProcessing > 0 && (
            <ProcessingLoader
              assetQuantity={this.state.assetsProcessing}
              onClose={() => this.setState({ assetsProcessing: 0 })}
            />
          )}
        </div>
      </SectionsContext.Provider>
    );
  }
}

ShowPage.propTypes = {
  assets: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({}))),
  approver: PropTypes.bool,
  editable: PropTypes.bool,
  defaultStrictSearch: PropTypes.bool,
  libraryName: PropTypes.string,
  pinsDropdownSetting: PropTypes.bool,
  sectionDropdownSetting: PropTypes.bool,
  defaultSortOrder: PropTypes.string,
  searchFilters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
  })),
  sections: PropTypes.arrayOf(PropTypes.shape({
    section_key: PropTypes.string,
  })),
  sectionsToShow: PropTypes.arrayOf(PropTypes.number),
  paginatePer: PropTypes.number.isRequired,
  printuiSectionId: PropTypes.number,
  lazyLoadCards: PropTypes.bool,
  enableContactSheets: PropTypes.bool
};

ShowPage.defaultProps = {
  assets: {},
  approver: false,
  editable: false,
  defaultStrictSearch: false,
  libraryName: "Brandfolder",
  pinsDropdownSetting: false,
  sectionDropdownSetting: false,
  defaultSortOrder: 'position ASC',
  searchFilters: [],
  sections: [],
  sectionsToShow: [],
  printuiSectionId: null,
  lazyLoadCards: true,
  enableContactSheets: false
};

export default ShowPage;
